import { useState, useEffect } from "react";
import { T } from "../styles/token.js";
import { useTheme } from "../context/ThemeContext";
import { api } from "../data/api";
import { isMultiChannelDevice } from "../data/deviceModels";
import Badge from "./Badge";
import Pill from "./Pill";
import { TH, TD } from "./TableWrap";

export default function AffiliateViewer({ affiliateId, onClose }) {
  const { mode } = useTheme();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]       = useState("details");

  useEffect(() => {
    api.affiliates.detail(affiliateId)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [affiliateId]);

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.35)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:400, fontFamily:"'DM Sans', system-ui, sans-serif" }}
    >
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, width:620, maxHeight:"82vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:"0 8px 32px rgba(0,0,0,0.15)" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"18px 20px", borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:T.ink }}>{data?.affiliate?.org_name || affiliateId}</div>
            <div style={{ fontSize:11, color:T.textSub, marginTop:2, fontFamily:"'IBM Plex Mono', monospace" }}>{affiliateId}</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {data?.affiliate && <Badge status={data.affiliate.status} />}
            <button onClick={onClose} style={{ background:"none", border:`1px solid ${T.border}`, borderRadius:6, padding:"4px 10px", fontSize:13, color:T.textDim, cursor:"pointer", fontFamily:"inherit" }}>×</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
          {[["details","Details"], ["channels","Channels"], ["devices","Devices"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{ padding:"10px 18px", fontSize:12, fontWeight: tab===key ? 600 : 400, color: tab===key ? T.ink : T.textSub, background:"none", border:"none", borderBottom:`2px solid ${tab===key ? T.ink : "transparent"}`, cursor:"pointer", fontFamily:"inherit", marginBottom:-1 }}>
              {label}
              {key === "channels" && data?.channels && <span style={{ marginLeft:5, fontSize:10, color:T.textDim }}>({data.channels.length})</span>}
              {key === "devices"  && data?.devices  && <span style={{ marginLeft:5, fontSize:10, color:T.textDim }}>({data.devices.length})</span>}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ overflowY:"auto", flex:1 }}>
          {loading ? (
            <div style={{ padding:40, textAlign:"center", fontSize:12, color:T.textDim }}>Loading…</div>
          ) : !data ? (
            <div style={{ padding:40, textAlign:"center", fontSize:12, color:T.red }}>Failed to load affiliate data.</div>
          ) : (
            <>
              {/* Details tab */}
              {tab === "details" && (
                <div style={{ padding:20 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
                    {[
                      ["Affiliate ID",   data.affiliate.id,            true],
                      ["Organization",   data.affiliate.org_name,      false],
                      ["Contact Name",   data.affiliate.contact_name,  false],
                      ["Contact Email",  data.affiliate.contact_email, false],
                      ["Region",         data.affiliate.region,        false],
                      ["Timezone",       data.affiliate.timezone,      false],
                      ["Status",         null,                         false],
                      ["Created",        data.affiliate.created_at,    false],
                    ].map(([k, v, mono]) => (
                      <div key={k} style={{ padding:"10px 0", borderBottom:`1px solid ${T.border}`, paddingRight:20 }}>
                        <div style={{ fontSize:10, color:T.textDim, textTransform:"uppercase", letterSpacing:"0.07em", fontWeight:600, marginBottom:4 }}>{k}</div>
                        {k === "Status" ? (
                          <Badge status={data.affiliate.status} />
                        ) : k === "Created" ? (
                          <div style={{ fontSize:12, color:T.text }}>{v ? new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}</div>
                        ) : (
                          <div style={{ fontSize:12, fontWeight:500, color: v ? T.ink : T.textDim, fontFamily: mono ? "'IBM Plex Mono', monospace" : "inherit" }}>{v || "—"}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Channels tab */}
              {tab === "channels" && (
                data.channels.length === 0 ? (
                  <div style={{ padding:24, textAlign:"center", fontSize:12, color:T.textDim }}>No channels assigned.</div>
                ) : (
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr style={{ background:T.surfaceAlt, position:"sticky", top:0 }}>
                        <TH>Channel ID</TH>
                        <TH>Display Name</TH>
                        <TH>Ch No.</TH>
                        <TH>Quality</TH>
                        <TH>Video Bitrate</TH>
                        <TH>Status</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {data.channels.map((ch, i) => (
                        <tr key={ch.id} style={{ borderBottom: i < data.channels.length-1 ? `1px solid ${T.border}` : "none" }}>
                          <TD><Pill label={ch.id} /></TD>
                          <TD style={{ fontWeight:500 }}>{ch.display_name}</TD>
                          <TD mono sub>{ch.channel_number || "—"}</TD>
                          <TD>
                            <span style={{ background: ch.quality==="HD" ? T.blueBg : T.surfaceAlt, color: ch.quality==="HD" ? T.blue : T.textSub, border:`1px solid ${ch.quality==="HD" ? T.blueBorder : T.border}`, borderRadius:4, padding:"2px 7px", fontSize:11, fontWeight:600 }}>
                              {ch.quality || "SD"}
                            </span>
                          </TD>
                          <TD mono sub>{ch.video_bitrate ? `${ch.video_bitrate} Mbps` : "—"}</TD>
                          <TD><Badge status={ch.status} /></TD>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}

              {/* Devices tab */}
              {tab === "devices" && (
                data.devices.length === 0 ? (
                  <div style={{ padding:24, textAlign:"center", fontSize:12, color:T.textDim }}>No devices registered.</div>
                ) : (
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr style={{ background:T.surfaceAlt, position:"sticky", top:0 }}>
                        <TH>Device ID</TH>
                        <TH>MAC Address</TH>
                        <TH>Model</TH>
                        <TH>Channel</TH>
                        <TH>Status</TH>
                        <TH>Last Seen</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {data.devices.map((dev, i) => {
                        const channelIds = isMultiChannelDevice(dev)
                          ? (Array.isArray(dev.channel_ids) ? dev.channel_ids : (dev.channel_id ? [dev.channel_id] : []))
                          : (dev.channel_id ? [dev.channel_id] : []);
                        return (
                        <tr key={dev.id} style={{ borderBottom: i < data.devices.length-1 ? `1px solid ${T.border}` : "none" }}>
                          <TD mono style={{ fontWeight:500 }}>{dev.id}</TD>
                          <TD mono sub>{dev.mac_address}</TD>
                          <TD sub>{dev.device_type === "self-srt-connect" ? "" : (dev.device_model || "—")}</TD>
                          <TD>
                            {channelIds.length === 0
                              ? <span style={{ fontSize:12, color:T.textDim }}>—</span>
                              : <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                                  {channelIds.map(chId => <Pill key={chId} label={chId} />)}
                                </div>
                            }
                          </TD>
                          <TD><Badge status={dev.status} /></TD>
                          <TD sub style={{ fontSize:11 }}>
                            {dev.last_seen ? new Date(dev.last_seen).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : "—"}
                          </TD>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:"12px 20px", borderTop:`1px solid ${T.border}`, display:"flex", justifyContent:"flex-end", flexShrink:0, background:T.surfaceAlt }}>
          <div style={{ fontSize:11, color:T.textDim }}>View only — use Edit to make changes</div>
        </div>
      </div>
    </div>
  );
}